import { Add, Delete, HighlightOff } from "@mui/icons-material";
import { Button, IconButton, Paper, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import { debounce, get, isNil } from "lodash";
import React from "react";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import FormTitle from "../../../common/js/components/FormTitle";
import { Search } from "../../../common/js/icons";
import searchConstants from "../search/constants";
import { us_states_options } from "../utils/constants";

const StyledPaperSpacing = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledShrinkButton = styled(Button)({
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    margin: 0,
    color: "#9e9e9e",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledCounter = styled("span")({
    border: "1px solid #2C99D2",
    borderRadius: "50%",
    display: "inline-block",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 600,
    width: "22px",
    height: "20px",
    color: "#2C99D2",
    paddingTop: "2px",
});

const StyledClearButton = styled(IconButton)({
    "&:hover": {
        backgroundColor: "transparent",
        color: "black",
    },
    padding: "0px",
});

const StyledCenterCell = styled("div")({
    textAlign: "center",
    fill: "#9e9e9e",
});

const StyledMoveIt = styled(Button)({
    marginRight: "20px",
    padding: 0,
    "&:hover": {
        background: "transparent",
    },
});

const StyledFlexBoxContainer = styled("div")({
    display: "flex",
    gap: "1vw",
});

const StyledFlexBoxItems = styled("div")({
    flex: "1 1 0",
});

const StyledReminderFlexBoxContainer = styled("div")({
    display: "flex",
    gap: "1vw",
    justifyContent: "space-between",
    alignItems: "center",
});

const AddContact = (props) => {
    const {
        addArrayElement,
        autocompleteResults,
        auth,
        contact,
        deleteArrayElement,
        disableInputs,
        errors,
        handleInput,
        handleSearchCompany,
        reminders,
        remindersActive,
        updateArrayElement,
    } = props;

    const selectCompany = (company) => {
        handleInput(`contact.company`, company);
    };

    const clearCompany = () => {
        handleInput(`contact.company`, {});
    };

    const handleDeleteReminder = (index) => {
        deleteArrayElement("contact.reminders", index);
    };

    const handleAddReminder = () => {
        addArrayElement("contact.reminders", {
            contactKey: get(props, "contact.contactKey"),
            owner: { username: auth.userProfile.user },
        });
    };

    const debounceSearchCompany = debounce((handleSearchCompany, term) => {
        handleSearchCompany(term, "CONTACT");
    }, searchConstants["DEBOUNCE_SEARCH_DELAY"]);

    return (
        <StyledFlexBoxContainer>
            <StyledFlexBoxItems>
                <StyledPaperSpacing>
                    <React.Fragment>
                        <FormTitle title="General Information" />

                        <hr style={{ margin: "0px -20px 25px", borderTop: "1px solid #eeeeee" }} />

                        <StyledFlexBoxContainer>
                            <BSInput
                                id="firstName"
                                label="First Name"
                                value={get(contact, `person.firstName`, "")}
                                error={get(errors, `person.firstName`, null)}
                                onChange={(e) => handleInput("contact.person.firstName", e.target.value)}
                                placeholder="Enter first name"
                                disabled={disableInputs === true}
                            />

                            <BSInput
                                id="lastName"
                                label="Last Name"
                                value={get(contact, `person.lastName`, "")}
                                error={get(errors, `person.lastName`, null)}
                                onChange={(e) => handleInput("contact.person.lastName", e.target.value)}
                                placeholder="Enter last name"
                                disabled={disableInputs === true}
                            />
                        </StyledFlexBoxContainer>

                        <StyledFlexBoxContainer>
                            <BSInput
                                id="phoneNumber"
                                label="Phone"
                                value={get(contact, `phoneNumber.phoneNumber`, "")}
                                error={get(errors, `phoneNumber.phoneNumber`, null)}
                                onChange={(e) => handleInput("contact.phoneNumber.phoneNumber", e.target.value)}
                                errorText="Invalid"
                                placeholder="Enter contact phone"
                                disabled={disableInputs === true}
                                phone={true}
                            />

                            <BSInput
                                id="emailAddress"
                                label="Email"
                                value={get(contact, `emailAddress`, "")}
                                error={get(errors, `emailAddress`, null)}
                                onChange={(e) => handleInput("contact.emailAddress", e.target.value)}
                                placeholder="Enter contact email"
                                disabled={disableInputs === true}
                            />
                        </StyledFlexBoxContainer>

                        <Autocomplete
                            id="company"
                            label="Company"
                            error={get(errors, `company.companyKey`, null)}
                            initialInputValue={`${get(contact, `company.displayName`, "")}`}
                            itemToString={(i) => (i == null ? "" : get(i, "displayName", ""))}
                            onSelect={(p) => selectCompany(p)}
                            placeholder="Enter company name"
                            searchFunction={(input) => debounceSearchCompany(handleSearchCompany, input)}
                            startAdornment={<Search />}
                            endAdornment={
                                contact.company?.companyKey && (
                                    <StyledClearButton onClick={clearCompany}>
                                        <HighlightOff style={{ fontSize: "16px" }} />
                                    </StyledClearButton>
                                )
                            }
                            suggestions={autocompleteResults}
                            disabled={disableInputs === true || !isNil(get(contact, `company.companyKey`, null))}
                            options={{
                                key: "companyKey",
                                display: (suggestion) => {
                                    return get(suggestion, "displayName", "");
                                },
                                displaySub: (suggestion) => {
                                    return get(suggestion, "mainAddress.address1", "");
                                },
                            }}
                        />

                        <BSInput
                            id="address1"
                            label="Address"
                            value={get(contact, `address.address1`, "")}
                            error={get(errors, `address.address1`, null)}
                            onChange={(e) => handleInput("contact.address.address1", e.target.value)}
                            errorText="Required"
                            placeholder="Enter address"
                            disabled={disableInputs === true}
                        />

                        <StyledFlexBoxContainer>
                            <div style={{ flex: "1 1 0" }}>
                                <BSInput
                                    id="city"
                                    label="City"
                                    value={get(contact, `address.city.city`, "")}
                                    error={get(errors, `address.city.city`, null)}
                                    onChange={(e) => handleInput("contact.address.city.city", e.target.value)}
                                    errorText="Required"
                                    placeholder="Enter city"
                                    disabled={disableInputs === true}
                                />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <BSSelect
                                    id="state"
                                    label="State"
                                    value={get(contact, `address.city.state.stateCode`, "")}
                                    error={get(errors, `address.city.state.stateCode`, null)}
                                    errorText="Required"
                                    onChange={(e) =>
                                        handleInput("contact.address.city.state.stateCode", e.target.value)
                                    }
                                    options={us_states_options}
                                    disabled={disableInputs === true}
                                />
                            </div>
                            <div style={{ flex: "1 1 0" }}>
                                <BSInput
                                    id="zipCode"
                                    label="Zip Code"
                                    value={get(contact, `address.zipCode.zipCode`, "")}
                                    error={get(errors, `address.zipCode.zipCode`, null)}
                                    onChange={(e) => handleInput("contact.address.zipCode.zipCode", e.target.value)}
                                    errorText="Invalid"
                                    placeholder="Enter zip code"
                                    disabled={disableInputs === true}
                                />
                            </div>
                        </StyledFlexBoxContainer>

                        <hr style={{ borderTop: "1px solid #eeeeee" }} />

                        <StyledReminderFlexBoxContainer>
                            <div>
                                <h4>Set a Reminder</h4>
                            </div>
                            <div>
                                <Switch
                                    checked={remindersActive ? true : false}
                                    onChange={(e) => handleInput("contact.remindersActiveFlg", e.target.checked)}
                                    value={remindersActive}
                                    color="primary"
                                />
                            </div>
                        </StyledReminderFlexBoxContainer>
                        {remindersActive && (
                            <React.Fragment>
                                {reminders &&
                                    reminders.map((reminder, index) => {
                                        return (
                                            <React.Fragment>
                                                <div
                                                    spacing={8}
                                                    style={{
                                                        display: "flex",
                                                        gap: "1vw",
                                                        alignItems: "center",
                                                        height: "90px",
                                                        marginTop: index === 0 ? "-30px" : 0,
                                                    }}
                                                >
                                                    <div style={{ marginLeft: "15px" }}>
                                                        <StyledCounter>{parseInt(index) + 1}</StyledCounter>
                                                    </div>
                                                    <BSInput
                                                        id="date"
                                                        errorText="Required"
                                                        placeholder="Enter date"
                                                        value={get(reminder, "date")}
                                                        onChange={(e) =>
                                                            updateArrayElement(
                                                                "contact.reminders",
                                                                index,
                                                                e.target.id,
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={disableInputs}
                                                        type="date"
                                                    />
                                                    <div style={{ flexBasis: 0, width: "22px" }}>
                                                        <span>at</span>
                                                    </div>
                                                    <BSInput
                                                        id="time"
                                                        type="time"
                                                        errorText="Required"
                                                        placeholder="Enter time"
                                                        value={get(reminder, "time")}
                                                        onChange={(e) =>
                                                            updateArrayElement(
                                                                "contact.reminders",
                                                                index,
                                                                e.target.id,
                                                                e.target.value
                                                            )
                                                        }
                                                        disabled={disableInputs}
                                                    />
                                                    <StyledCenterCell>
                                                        {disableInputs !== true && (
                                                            <StyledShrinkButton
                                                                onClick={() => handleDeleteReminder(index)}
                                                            >
                                                                <Delete />
                                                            </StyledShrinkButton>
                                                        )}
                                                    </StyledCenterCell>
                                                </div>

                                                <BSInput
                                                    id="reminderNotes"
                                                    label="Notes"
                                                    value={get(reminder, "reminderNotes")}
                                                    onChange={(e) =>
                                                        updateArrayElement(
                                                            "contact.reminders",
                                                            index,
                                                            e.target.id,
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Enter notes"
                                                    disabled={disableInputs === true}
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                                <StyledMoveIt color="primary" onClick={() => handleAddReminder()}>
                                    <Add fill="#4b94c1" /> Add Reminder
                                </StyledMoveIt>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                </StyledPaperSpacing>
            </StyledFlexBoxItems>
            <StyledFlexBoxItems>
                <StyledPaperSpacing>
                    <FormTitle title="Contact Notes" />

                    <hr style={{ margin: "0px -20px", borderTop: "1px solid #eeeeee" }} />

                    <BSInput
                        id="contactNotes"
                        placeholder="Enter your notes here."
                        value={get(contact, "contactNotes", "")}
                        multiline
                        rows={8}
                        onChange={(e) => handleInput("contact.contactNotes", e.target.value)}
                        disabled={disableInputs}
                        extraInputStyles={{ marginBottom: "0px" }}
                    />
                </StyledPaperSpacing>
            </StyledFlexBoxItems>
        </StyledFlexBoxContainer>
    );
};

export default AddContact;
